import { useCallback } from 'react';
import { useProfile } from './useProfile';
import { useExchangeRates } from './useExchangeRates';

export interface RegionalSettings {
  country: string;
  countryCode: string;
  currency: string;
  currencySymbol: string;
}

export interface FormatPriceOptions {
  showOriginal?: boolean; // Show original price in parentheses when converted
  compact?: boolean; // Use compact notation for large numbers
}

export interface FormattedPrice {
  formatted: string; // The formatted price string
  isConverted: boolean; // Whether conversion was applied
  originalFormatted?: string; // Original price if converted (e.g., "~$100 USD")
  convertedValue?: number; // The numeric converted value
}

// Country data with ISO codes for API calls
// Ordered: GB, US first → mainland Europe alphabetically → rest of world alphabetically
const MAINLAND_EUROPE_CODES = new Set([
  'AT','BE','HR','CZ','DK','FI','FR','DE','GR','HU','IE','IT',
  'NL','NO','PL','PT','RO','RU','ES','SE','CH','TR','UA',
]);

export const COUNTRIES = [
  // — Priority —
  { name: 'United Kingdom', code: 'GB', currency: 'GBP', symbol: '£' },
  { name: 'United States', code: 'US', currency: 'USD', symbol: '$' },
  // — Mainland Europe (alphabetical) —
  { name: 'Austria', code: 'AT', currency: 'EUR', symbol: '€' },
  { name: 'Belgium', code: 'BE', currency: 'EUR', symbol: '€' },
  { name: 'Croatia', code: 'HR', currency: 'EUR', symbol: '€' },
  { name: 'Czech Republic', code: 'CZ', currency: 'CZK', symbol: 'Kč' },
  { name: 'Denmark', code: 'DK', currency: 'DKK', symbol: 'kr' },
  { name: 'Finland', code: 'FI', currency: 'EUR', symbol: '€' },
  { name: 'France', code: 'FR', currency: 'EUR', symbol: '€' },
  { name: 'Germany', code: 'DE', currency: 'EUR', symbol: '€' },
  { name: 'Greece', code: 'GR', currency: 'EUR', symbol: '€' },
  { name: 'Hungary', code: 'HU', currency: 'HUF', symbol: 'Ft' },
  { name: 'Ireland', code: 'IE', currency: 'EUR', symbol: '€' },
  { name: 'Italy', code: 'IT', currency: 'EUR', symbol: '€' },
  { name: 'Netherlands', code: 'NL', currency: 'EUR', symbol: '€' },
  { name: 'Norway', code: 'NO', currency: 'NOK', symbol: 'kr' },
  { name: 'Poland', code: 'PL', currency: 'PLN', symbol: 'zł' },
  { name: 'Portugal', code: 'PT', currency: 'EUR', symbol: '€' },
  { name: 'Romania', code: 'RO', currency: 'RON', symbol: 'lei' },
  { name: 'Russia', code: 'RU', currency: 'RUB', symbol: '₽' },
  { name: 'Spain', code: 'ES', currency: 'EUR', symbol: '€' },
  { name: 'Sweden', code: 'SE', currency: 'SEK', symbol: 'kr' },
  { name: 'Switzerland', code: 'CH', currency: 'CHF', symbol: 'CHF' },
  { name: 'Turkey', code: 'TR', currency: 'TRY', symbol: '₺' },
  { name: 'Ukraine', code: 'UA', currency: 'UAH', symbol: '₴' },
  // — Rest of world (alphabetical) —
  { name: 'Argentina', code: 'AR', currency: 'ARS', symbol: 'AR$' },
  { name: 'Australia', code: 'AU', currency: 'AUD', symbol: 'A$' },
  { name: 'Bangladesh', code: 'BD', currency: 'BDT', symbol: '৳' },
  { name: 'Brazil', code: 'BR', currency: 'BRL', symbol: 'R$' },
  { name: 'Canada', code: 'CA', currency: 'CAD', symbol: 'CA$' },
  { name: 'Chile', code: 'CL', currency: 'CLP', symbol: 'CLP$' },
  { name: 'China', code: 'CN', currency: 'CNY', symbol: '¥' },
  { name: 'Colombia', code: 'CO', currency: 'COP', symbol: 'COL$' },
  { name: 'Egypt', code: 'EG', currency: 'EGP', symbol: 'E£' },
  { name: 'Hong Kong', code: 'HK', currency: 'HKD', symbol: 'HK$' },
  { name: 'India', code: 'IN', currency: 'INR', symbol: '₹' },
  { name: 'Indonesia', code: 'ID', currency: 'IDR', symbol: 'Rp' },
  { name: 'Israel', code: 'IL', currency: 'ILS', symbol: '₪' },
  { name: 'Japan', code: 'JP', currency: 'JPY', symbol: '¥' },
  { name: 'Malaysia', code: 'MY', currency: 'MYR', symbol: 'RM' },
  { name: 'Mexico', code: 'MX', currency: 'MXN', symbol: 'MX$' },
  { name: 'New Zealand', code: 'NZ', currency: 'NZD', symbol: 'NZ$' },
  { name: 'Nigeria', code: 'NG', currency: 'NGN', symbol: '₦' },
  { name: 'Pakistan', code: 'PK', currency: 'PKR', symbol: '₨' },
  { name: 'Peru', code: 'PE', currency: 'PEN', symbol: 'S/' },
  { name: 'Philippines', code: 'PH', currency: 'PHP', symbol: '₱' },
  { name: 'Qatar', code: 'QA', currency: 'QAR', symbol: 'ر.ق' },
  { name: 'Saudi Arabia', code: 'SA', currency: 'SAR', symbol: 'ر.س' },
  { name: 'Singapore', code: 'SG', currency: 'SGD', symbol: 'S$' },
  { name: 'South Africa', code: 'ZA', currency: 'ZAR', symbol: 'R' },
  { name: 'South Korea', code: 'KR', currency: 'KRW', symbol: '₩' },
  { name: 'Sri Lanka', code: 'LK', currency: 'LKR', symbol: 'Rs' },
  { name: 'Taiwan', code: 'TW', currency: 'TWD', symbol: 'NT$' },
  { name: 'Thailand', code: 'TH', currency: 'THB', symbol: '฿' },
  { name: 'United Arab Emirates', code: 'AE', currency: 'AED', symbol: 'د.إ' },
  { name: 'Vietnam', code: 'VN', currency: 'VND', symbol: '₫' },
];

export const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Złoty' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
];

/** Convert ISO country code to flag emoji */
export function countryFlag(code: string): string {
  return code
    .toUpperCase()
    .split('')
    .map(c => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');
}

export function getCountryByCode(code: string) {
  return COUNTRIES.find(c => c.code === code) || COUNTRIES[0];
}

export function getCurrencyByCode(code: string) {
  return CURRENCIES.find(c => c.code === code) || CURRENCIES[0];
}

export function useRegionalSettings() {
  const { profile, loading: profileLoading, updateProfile } = useProfile();
  const { convertPrice: convertCurrency, isLoading: ratesLoading, canConvert } = useExchangeRates();

  // Get current settings with defaults
  const countryCode = profile?.country || 'US';
  const currencyCode = profile?.currency || 'USD';

  const country = getCountryByCode(countryCode);
  const currency = getCurrencyByCode(currencyCode);

  const settings: RegionalSettings = {
    country: country.name,
    countryCode: country.code,
    currency: currency.code,
    currencySymbol: currency.symbol,
  };

  const updateCountry = useCallback(async (code: string) => {
    if (!profile) return;
    const matchedCountry = getCountryByCode(code);
    // Auto-sync currency to match the selected country
    await updateProfile({ country: code, currency: matchedCountry.currency });
  }, [profile, updateProfile]);

  const updateCurrency = useCallback(async (code: string) => {
    if (!profile) return;
    await updateProfile({ currency: code });
  }, [profile, updateProfile]);

  /**
   * Format a price with automatic currency conversion if needed
   * @param price - The price value
   * @param sourceCurrency - The currency the price is in (defaults to user's preferred currency)
   * @param options - Formatting options
   * @returns Formatted price object with conversion info
   */
  const formatPriceDetailed = useCallback((
    price: number | null | undefined,
    sourceCurrency?: string,
    options: FormatPriceOptions = {}
  ): FormattedPrice | null => {
    if (price === null || price === undefined) return null;

    const from = (sourceCurrency || settings.currency).toUpperCase();
    const to = settings.currency.toUpperCase();
    const targetCurrencyData = getCurrencyByCode(to);
    const sourceCurrencyData = getCurrencyByCode(from);

    // No conversion needed - same currency
    if (from === to) {
      const formatted = `${targetCurrencyData.symbol}${price.toLocaleString(undefined, { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      })}`;
      return {
        formatted,
        isConverted: false,
      };
    }

    // Try to convert
    const convertedValue = convertCurrency(price, from, to);
    
    if (convertedValue !== null) {
      const formatted = `${targetCurrencyData.symbol}${convertedValue.toLocaleString(undefined, { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      })}`;

      const result: FormattedPrice = {
        formatted,
        isConverted: true,
        convertedValue,
      };

      if (options.showOriginal) {
        result.originalFormatted = `~${sourceCurrencyData.symbol}${price.toLocaleString(undefined, { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        })} ${from}`;
      }

      return result;
    }

    // Fallback: conversion not available, show original with source currency
    const formatted = `${sourceCurrencyData.symbol}${price.toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
    return {
      formatted,
      isConverted: false,
    };
  }, [settings.currency, convertCurrency]);

  /**
   * Simple format function that returns just the formatted string
   * This is the primary function most components will use
   */
  const formatPrice = useCallback((
    price: number | null | undefined, 
    sourceCurrency?: string
  ): string | null => {
    const result = formatPriceDetailed(price, sourceCurrency);
    return result?.formatted || null;
  }, [formatPriceDetailed]);

  /**
   * Convert a price value between currencies (returns just the number)
   */
  const convertPrice = useCallback((
    price: number,
    fromCurrency: string,
    toCurrency?: string
  ): number => {
    const to = toCurrency || settings.currency;
    if (fromCurrency.toUpperCase() === to.toUpperCase()) return price;
    
    const converted = convertCurrency(price, fromCurrency, to);
    return converted ?? price; // Fallback to original if conversion fails
  }, [settings.currency, convertCurrency]);

  return {
    settings,
    loading: profileLoading || ratesLoading,
    updateCountry,
    updateCurrency,
    formatPrice,
    formatPriceDetailed,
    convertPrice,
    canConvert,
    countries: COUNTRIES,
    currencies: CURRENCIES,
  };
}
